/**
 * The Ustawienia tab's phone: the trainer's own free training starts over the horizon
 * (`GET /trainer-availability/slots`), read into what a client would see by `model/clientPreview`.
 *
 * Its read has its own gate and its own box: the phone stands from the first frame, and only its
 * screen waits. A policy change on the tab repaints the phone at once from the policy on screen
 * (notice, horizon, cancellation), while the slot read — which a changed length, step or buffer
 * alters on the server — is read again by the write's `readBookingAgain`.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { TrainWithMeModulesBookingDtosResponsesBookingPolicyResponse as BookingPolicy } from '~/shared/api/generated/model';
import { useSkeletonGate, type SkeletonGate } from '~/shared/lib/useSkeletonGate';

import { trainerSlotsQueryOptions } from '../api/bookingQueries';
import { clientPreview, type ClientPreview } from '../model/clientPreview';
import { type ReadState } from '../model/sessionCredit';
import { previewReadRange } from '../model/scheduleDeviations';
import { wallReading } from '../model/trainerClock';

export interface ClientPreviewState {
  readonly gate: SkeletonGate;
  /** `null` while the gate waits; `failed` when the read did not answer. */
  readonly preview: ClientPreview | { readonly kind: 'failed' } | null;
  readonly durationMinutes: number;
}

export function useClientPreview(
  policy: ReadState<BookingPolicy>,
  context: {
    readonly nowMs: number;
    readonly zone: string;
    /** Whether the trainer's zone is stored, as the hours read says (R3). */
    readonly zonePublished: ReadState<boolean>;
  },
): ClientPreviewState {
  const today = wallReading(context.nowMs, context.zone).day;
  const ready = policy.kind === 'ready' ? policy.data : undefined;
  const slots = useQuery({
    ...trainerSlotsQueryOptions(
      previewReadRange(today, ready?.horizonDays ?? 30),
      ready !== undefined,
    ),
    placeholderData: keepPreviousData,
  });
  // A disabled read is `pending` for ever, so the wait is the policy's first answer, then the
  // slots'. A policy that FAILED ends the wait too — the phone says it did not load.
  const gate = useSkeletonGate(
    policy.kind === 'pending' ||
      context.zonePublished.kind === 'pending' ||
      (ready !== undefined && slots.data === undefined && slots.errorUpdateCount === 0),
  );
  return {
    gate,
    preview: gate.waiting
      ? null
      : ready === undefined || slots.data === undefined || context.zonePublished.kind !== 'ready'
        ? { kind: 'failed' }
        : clientPreview({
            slots: slots.data,
            policy: ready,
            nowMs: context.nowMs,
            zone: context.zone,
            zonePublished: context.zonePublished.data,
            today,
          }),
    durationMinutes: slots.data?.durationMinutes ?? ready?.trainingMinutes ?? 0,
  };
}
